import { getProviderLabel } from '../../constants/providers'
import { useI18n } from '../../i18n'

const StopIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <rect x="6" y="6" width="12" height="12" rx="1" />
  </svg>
)

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
)

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

/**
 * Один слот провайдера в карточке модели.
 *
 * @param {{ link, modelId, modelEmail, modelName, connectionError, connectingModelId, cancellingModelId, cancellingProvider, disconnectingLink, onConnect, onCancelConnection, onDisconnectConfirm }} props
 */
export default function ModelProviderSlotRow({
  link,
  modelId,
  modelName,
  connectionError,
  connectingModelId,
  cancellingModelId,
  cancellingProvider,
  disconnectingLink,
  onConnect,
  onCancelConnection,
  onDisconnectConfirm,
}) {
  const { t } = useI18n()

  const isConnecting = link.connection_status === 'connecting'
  const needConnect = link.connection_status === 'pending' || link.connection_status === 'failed'
  const isDisconnecting =
    disconnectingLink?.modelId === modelId && disconnectingLink?.linkId === link.id
  const errorText =
    connectionError ||
    (link.connection_status === 'failed' ? link.connection_error : null)

  let centerNode = null
  if (link.connection_status === 'connected') {
    centerNode = (
      <span className="model-card-provider-status model-card-provider-status-ok">
        {t('models.connection.connected')}
      </span>
    )
  } else if (isConnecting) {
    centerNode = (
      <span className="model-card-provider-status model-card-provider-status-connecting">
        <span className="model-card-provider-spinner" aria-hidden />
        {t('models.connection.connecting')}
      </span>
    )
  } else if (errorText) {
    centerNode = (
      <span className="model-card-connection-error-tooltip">
        <span className="model-card-connection-error-label">{t('common.error')}</span>
        <span className="model-card-connection-error-tooltip-text">{errorText}</span>
      </span>
    )
  } else if (link.connection_status === 'pending') {
    centerNode = (
      <span className="model-card-provider-status model-card-provider-status-pending">
        {t('models.connection.pending')}
      </span>
    )
  }

  let actionNode = <span className="model-card-provider-action-placeholder" aria-hidden />
  if (isConnecting) {
    actionNode = (
      <button
        type="button"
        className="model-card-provider-stop-icon"
        title={t('models.connection.stop')}
        aria-label={t('models.connection.stop')}
        disabled={cancellingModelId === modelId && cancellingProvider === link.provider}
        onClick={() => onCancelConnection(modelId, link.provider)}
      >
        <StopIcon />
      </button>
    )
  } else if (link.connection_status === 'connected') {
    actionNode = (
      <button
        type="button"
        className="model-card-provider-disconnect-icon"
        title={t('models.connection.unlink')}
        aria-label={t('models.connection.unlink')}
        disabled={isDisconnecting}
        onClick={() =>
          onDisconnectConfirm({
            modelId,
            linkId: link.id,
            modelName: modelName || '—',
            providerLabel: getProviderLabel(link.provider),
          })
        }
      >
        <TrashIcon />
      </button>
    )
  } else if (needConnect) {
    actionNode = (
      <button
        type="button"
        className="model-card-provider-connect-icon model-card-icon-connect"
        onClick={() => onConnect(modelId)}
        disabled={connectingModelId === modelId}
        title={
          connectingModelId === modelId
            ? t('models.connection.connecting')
            : t('models.connection.connect')
        }
        aria-label={
          connectingModelId === modelId
            ? t('models.connection.connecting')
            : t('models.connection.connect')
        }
      >
        <LinkIcon />
      </button>
    )
  }

  return (
    <div
      key={link.id != null ? link.id : link.provider}
      className={`model-card-provider-row ${isConnecting ? 'model-card-provider-row--connecting' : ''}`}
    >
      <span className="model-card-provider-name">{getProviderLabel(link.provider)}</span>
      <div className="model-card-provider-center">{centerNode}</div>
      <div className="model-card-provider-action">{actionNode}</div>
    </div>
  )
}
